import { useMutationCandidateFillInBirthName } from "@/api/candidate/candidate";
import { keyFactory } from "@/api/keyFactory";
import { nestErrorMessage } from "@/api/nest-error";
import { Button } from "@/components/atoms/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/atoms/Dialog";
import { Form } from "@/components/atoms/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { formatFieldCapital } from "./Formatters/field-capitals";
import { FieldMandatoryType } from "./Formatters/mandatory-type";
import { PersonalInfoField } from "./PersonalInfoFields";
import { registrationPersonalInfoSchema } from "./PersonalInfoFormSchema";
import TextInputField from "./TextInputField";

const birthNameFormSchema = z.object({
  personalInfo: registrationPersonalInfoSchema.pick({ birthName: true }),
});

interface BirthNameDialogProps {
  isOpen: boolean;
  close: () => void;
  candidateId?: number;
  onFilledIn?: () => void;
}

const BirthNameDialog = ({
  isOpen,
  close,
  candidateId,
  onFilledIn,
}: BirthNameDialogProps) => {
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof birthNameFormSchema>>({
    resolver: zodResolver(birthNameFormSchema),
    defaultValues: { personalInfo: { birthName: "" } },
  });

  const fillInBirthNameMutation = useMutationCandidateFillInBirthName(
    candidateId,
    {
      onSuccess: () => {
        toast.success("Nom de naissance enregistré");
        close();
        onFilledIn?.();
      },
      onError: (error) =>
        toast.error("Erreur", { description: nestErrorMessage(error) }),
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: keyFactory.candidate.info(candidateId),
        }),
    },
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nom de naissance</DialogTitle>
          <DialogDescription>
            {candidateId
              ? "Il est nécessaire pour inscrire ce candidat à une session d’examen. Indiquez le nom figurant sur son acte de naissance."
              : "Il est nécessaire pour vous inscrire à une session d’examen. Indiquez le nom figurant sur votre acte de naissance : une fois enregistré, seul le CIFMD pourra le modifier."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(({ personalInfo }) =>
              fillInBirthNameMutation.mutate(personalInfo.birthName),
            )}
            className="flex flex-col gap-6"
          >
            <TextInputField
              title="Nom de naissance"
              type={PersonalInfoField.BirthName}
              fieldMandatoryType={FieldMandatoryType.MANDATORY}
              formattingFunction={formatFieldCapital}
              modifiers={{
                disabledFields: [],
                hiddenFields: [],
                unmodifiableToolTipFields: [],
              }}
            />
            <Button type="submit" disabled={fillInBirthNameMutation.isPending}>
              {fillInBirthNameMutation.isPending
                ? "Enregistrement..."
                : "Enregistrer"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BirthNameDialog;
